import { FC, useEffect, useState } from "react";

import { Box } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

import { ListenerTagRow } from "@/components/Procedures/Checklist/ListenerTagRow";
import { LiveTagStyling } from "@/components/Procedures/Checklist/utils/Types";

import { ListenerTag } from "@/model/Procedure/ListenerTag";
import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";

interface StepMeasurementListenersProps {
	activeSchemeId: number | undefined;
	autoMode: boolean;
	listenerTags: Array<ListenerTag>;
	showMeasurements: boolean;
	step: ProcedureNodeStep;
	setShowMeasurements: (show: boolean) => void;
	isActiveStep: boolean;
}

const StepMeasurementListeners: FC<StepMeasurementListenersProps> = (props) => {
	const theme = useTheme();
	const defaultStyling = {
		container: {
			background: "grey",
			color: "white",
		},
	};
	const [ListenerTagRowStyling, setListenerTagRowStyling] =
		useState<LiveTagStyling>(defaultStyling);

	const alertStyling = {
		container: {
			background: "#ff969a",
		},
		pulseAnimation: {},
	};

	const completeStyling = {
		container: {
			background: theme.palette.success.light,
			color: "white",
		},
		pulseAnimation: {},
	};

	const waitStyling = {
		container: {
			background: "#f9e488",
		},
		pulseAnimation: {
			content: '""',
			position: "absolute",
			left: "0",
			height: "101%",
			width: "101%",
			transform: "translateX(-100%)",
			background:
				"linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0))",
			animation: "pulseAnimation 1.5s linear infinite",
			"@keyframes pulseAnimation": {
				"0%": {
					transform: "translateX(-100%)",
				},
				"100%": {
					transform: "translateX(100%)",
				},
			},
		},
	};

	const skipStyling = {
		container: {
			background: "rgba(0, 0, 0, 0.12)",
		},
	};

	useEffect(() => {
		if (props.step.IsSkipped || props.step.IsOverridden) {
			setListenerTagRowStyling(skipStyling);
			return;
		} else if (props.step.Status) {
			setListenerTagRowStyling(completeStyling);
			return;
		} else if (!props.isActiveStep && !props.step.Status) {
			setListenerTagRowStyling(defaultStyling);
			return;
		} else if (
			props.isActiveStep &&
			!props.step.Status &&
			props.step.ListenerTags.some((x) => x.CurrentValue === null)
		) {
			setListenerTagRowStyling(alertStyling);
			return;
		} else if (props.isActiveStep && !props.step.Status) {
			setListenerTagRowStyling(waitStyling);
		}
	}, [props.step.Status, props.step.ListenerTags, props.isActiveStep]);

	if (props.step.ListenerTags.length === 0 || !props.showMeasurements) {
		return <></>;
	}

	return (
		<TagContainer>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: "1rem",
				}}
				className="grid-row-signal-container"
			>
				{props.step.ListenerTags.map((listenerTag, key) => (
					<ListenerTagRow
						key={key}
						listenerTag={listenerTag}
						step={props.step}
						isLast={key === props.step.ListenerTags.length - 1}
						checklistId={props.activeSchemeId}
						styling={ListenerTagRowStyling}
					/>
				))}
			</Box>
		</TagContainer>
	);
};

export default StepMeasurementListeners;

export const TagContainer = styled(Box)({
	width: "100%",
	position: "relative",
	borderRadius: 4,
	padding: "2px 5px",
	margin: "0 auto",
});
